import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage, listAllProjects, listProjectsByStatus } from '../../api';
import { paths } from '../../app/paths';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { formatDate } from '../../lib/format';
import { Button, Card, EmptyState, ErrorState, Pagination, Skeleton, StatusBadge, Table, Tabs, Td, Th, TextField } from '../../ui';
import type { Project } from '../../types';
import { NewProjectDialog } from './NewProjectDialog';

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'draft', label: 'Drafts' }, // the backend stores the singular "draft"
  { key: 'closed', label: 'Closed' },
];
const PAGE_SIZE = 10;

const matches = (project: Project, q: string) => {
  const needle = q.toLowerCase();
  return String(project.name || '').toLowerCase().includes(needle) || String(project.projectId ?? '').includes(needle);
};

export function ProjectsPage() {
  const [tab, setTab] = useState('active');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const query = useDebouncedValue(search.trim(), 300);
  const searching = query.length > 0;

  // Normal browsing: server-side pagination per status. Searching: the current backend only has an
  // exact-name search, so (like the legacy UI) load all campaigns once and filter them here.
  const paged = useAsyncData<{ items: Project[]; total: number }>((signal) => listProjectsByStatus(tab, page, PAGE_SIZE, { signal }), [tab, page], { enabled: !searching });
  const all = useAsyncData<Project[]>((signal) => listAllProjects({ signal }), [], { enabled: searching });

  const source = searching ? all : paged;
  const rows = useMemo(() => {
    if (searching) return (all.data || []).filter((p) => matches(p, query));
    return paged.data ? paged.data.items : [];
  }, [searching, all.data, paged.data, query]);
  const total = searching ? rows.length : paged.data ? paged.data.total : 0;

  const changeTab = (key: string) => { setTab(key); setPage(1); };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink">Campaigns</h1>
          <p className="mt-1 text-sm text-muted">Manage studies, target groups and fieldwork.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>＋ New campaign</Button>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        {searching ? <p className="text-sm text-muted" role="status">{rows.length} result{rows.length === 1 ? '' : 's'} for “{query}” across all statuses</p> : <Tabs tabs={TABS} value={tab} onChange={changeTab} label="Campaign status" />}
        <div className="w-full sm:w-72">
          <TextField label="Search campaigns" type="search" placeholder="Search by name or ID…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <Card className="overflow-hidden">
        {source.loading && !source.data ? (
          <div className="space-y-3 p-4" aria-busy="true" aria-label="Loading campaigns">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : source.error ? (
          <ErrorState title="Could not load campaigns" message={errorMessage(source.error)} onRetry={source.reload} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={searching ? 'No matching campaigns' : `No ${tab} campaigns`}
            description={searching ? 'Try a different name or ID.' : 'Campaigns you create will appear here.'}
            action={!searching && tab === 'active' ? <Button size="sm" onClick={() => setDialogOpen(true)}>Create your first campaign</Button> : null}
          />
        ) : (
          <Table caption="Campaigns">
            <thead>
              <tr><Th>ID</Th><Th>Campaign</Th><Th>Status</Th><Th>Categories</Th><Th numeric>Target groups</Th><Th>Starts</Th></tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="hover:bg-canvas">
                  <Td className="text-muted">{p.projectId ?? '—'}</Td>
                  <Td><Link to={paths.project(p.id)} className="font-semibold text-brand-700 hover:underline">{p.name || 'Untitled campaign'}</Link></Td>
                  <Td><StatusBadge status={p.projectStatus} /></Td>
                  <Td className="text-muted">{(p.projectCategoryId || []).map((c) => c.name).filter(Boolean).join(', ') || '—'}</Td>
                  <Td numeric>{(p.targetGroups || []).length}</Td>
                  <Td className="text-muted">{formatDate(p.startDate)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {!searching && !source.error && (source.data || source.loading === false) && total > 0 && (
          <div className="border-t border-line">
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} disabled={source.loading} />
          </div>
        )}
      </Card>

      <NewProjectDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
